import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import type {
  GestureSystem,
  GestureCoordinatorLike,
  EventDetailsManagerLike,
} from "./gesture-system.ts";
import { GlobalGestureStateManager } from "./global-gesture-state.ts";

/**
 * Coordinates gesture state and menu visibility across gesture systems.
 *
 * Content-agnostic — doesn't know about events, tasks, or any specific
 * content type. Systems register/unregister, and the coordinator ensures
 * mutual menu exclusion and click-outside dismissal.
 */
export class GestureCoordinator implements GestureCoordinatorLike {
  #systems: Set<GestureSystem> = new Set();
  #registeredElements: Set<HTMLElement> = new Set();
  #clickOutsideCache: WeakMap<HTMLElement, boolean> = new WeakMap();
  #handlingClickOutside = false;

  /** Component types that should not trigger dismissal. */
  readonly #whitelistTypes: Function[];
  /** Context attribute values that should not trigger dismissal. */
  readonly #whitelistContexts: string[];

  readonly globalState: GlobalGestureStateManager =
    new GlobalGestureStateManager();

  #_eventDetailsManager: Option<EventDetailsManagerLike> = null;

  constructor(
    config: Option<{
      whitelistTypes?: Function[];
      whitelistContexts?: string[];
    }>,
  ) {
    this.#whitelistTypes = config?.whitelistTypes ?? [];
    this.#whitelistContexts = config?.whitelistContexts ?? [];
  }

  // -- Event details manager (content-specific, optional) --

  setEventDetailsManager(manager: EventDetailsManagerLike): void {
    this.#_eventDetailsManager = manager;
  }

  get eventDetailsManager(): EventDetailsManagerLike {
    return nonnull(this.#_eventDetailsManager);
  }

  // -- System registration --

  register(system: GestureSystem): void {
    this.#systems.add(system);
  }

  unregister(system: GestureSystem): void {
    this.#systems.delete(system);
  }

  notifyMenuOpened(source: GestureSystem): void {
    for (const system of this.#systems) {
      if (system !== source) system.closeAllMenus();
    }
  }

  closeAll(): void {
    // Systems first: their hide() paths unregister their own global states,
    // so dismissAll() below only sweeps delegate-owned leftovers (task /
    // scheduling-link popovers that have no GestureSystem of their own).
    for (const system of this.#systems) system.closeAllMenus();
    this.globalState.dismissAll();
  }

  // -- Click outside --

  registerClickOutside($element: HTMLElement): void {
    this.#registeredElements.add($element);
    GESTURE_MANAGER.setOnClickOutside($element, ($target) => {
      this.#handleClickOutside($target ?? null);
    });
  }

  unregisterClickOutside($element: HTMLElement): void {
    this.#registeredElements.delete($element);
    GESTURE_MANAGER.clearOnClickOutside($element);
  }

  #handleClickOutside($target: Option<HTMLElement>): void {
    if (this.#handlingClickOutside) return;
    this.#handlingClickOutside = true;
    try {
      this.#_handleClickOutside($target);
    } finally {
      this.#handlingClickOutside = false;
    }
  }

  #_handleClickOutside($target: Option<HTMLElement>): void {
    if (!$target) {
      this.closeAll();
      return;
    }

    const cached = this.#clickOutsideCache.get($target);
    if (cached != null) {
      if (cached) this.closeAll();
      return;
    }

    for (const $el of this.#registeredElements) {
      if ($el.contains($target)) {
        this.#clickOutsideCache.set($target, false);
        return;
      }
    }

    const $ancestors = DomUtils.getAncestors($target);
    for (const $ancestor of [$target, ...$ancestors]) {
      if ($ancestor instanceof Window) break;

      const context = ($ancestor as HTMLElement).getAttribute?.("context");
      if (context && this.#whitelistContexts.includes(context)) {
        this.#clickOutsideCache.set($target, false);
        return;
      }

      for (const W of this.#whitelistTypes) {
        if ($ancestor instanceof W) {
          this.#clickOutsideCache.set($target, false);
          return;
        }
      }
    }

    this.#clickOutsideCache.set($target, true);
    this.closeAll();
  }
}
